import { useEffect, useRef } from 'react';
import { useReviseSession } from '@/app/providers/ReviseSessionContext';
import { useConversation } from '@/app/providers/ConversationContext';
import { useChatDock } from '@/app/providers/ChatDockContext';
import { useActionApprovals } from '@/common/hooks/useActionApprovals';

/**
 * Revise-session reconciler (Automations v2 §7.5 — "closing the revise session returns to strict
 * single-open"). A single effect in a STABLE owner (MainLayout, mounted for the whole app session)
 * that ends the Revise session on EVERY terminal path — decision, close-chat, and switch — not only
 * Discard.
 *
 * Why it's needed: `endRevise()` used to fire only from the card's Discard button, but the happy
 * path (Send / Save & send) decides the approval WITHOUT ending the session. Once the approval
 * leaves the pending queue the card UNMOUNTS, so a card-local cleanup can't run — and `reviseActive`
 * lives in the app-level provider, so it stays stuck `true` against a now-decided approval. That
 * permanently defeats W6 strict-single-open (`useChatNotificationExclusivity` keeps yielding) and
 * can strand `.chat-box.is-revise-split` with an empty 50/50 column, recoverable only by reload.
 *
 * The card also ends the session inline on Send / Save & send / Discard for an instant transition;
 * this reconciler is the authoritative safety net that additionally covers expiry, decisions made
 * from another surface, and — §7.5's remaining clause (#5424) — the user closing or switching away
 * from the revise chat WITHOUT deciding the draft (the card stays mounted and reachable in that
 * case, but `reviseActive`/`.is-revise-split` must not stay stuck against it either).
 *
 * Decision path: observes the SAME shared approvals query (same query key) the Activity Center
 * reads, and gates that query on `reviseActive` (`enabled: reviseActive`) so it NEVER polls idle:
 * `shouldEndByDecision` is always false when no session is active, so the query is only needed
 * during one. During a session notifications are open, so the Activity Center already observes
 * that key — the gated observer just dedupes with it, adding no background request. The transition
 * it must catch (the revised approval leaving the pending queue on expiry / a decision from another
 * surface) happens while `reviseActive` is still true — i.e. while the query is enabled — so gating
 * does not defeat the backstop. Only ends the session once the query has actually settled
 * (`isSuccess`), so a transient empty list (first fetch after begin, a workspace switch mid-fetch)
 * can never spuriously close an active revise.
 *
 * Close/switch path: `reviseConversationId` starts as the TEMP `conversation-*` id `beginRevise` was
 * called with and is swapped for the real id by the seed listener (useChatWindowEvents) once its
 * create resolves. Comparing a temp id against `activeConversation.id` (which is ALSO briefly the
 * same temp id, then a real one once ITS OWN create resolves) would be a false "switch" the instant
 * the id types no longer line up mid-creation — so the SWITCH half of this path conservatively
 * no-ops until `reviseConversationId` has settled to a real id. Once settled, ending on "the active
 * conversation is a DIFFERENT id" restores strict single-open exactly as §7.5 describes, without
 * needing the approval to be decided at all.
 *
 * The CLOSE half (the chat dock docking to the header launcher) is judged UNCONDITIONALLY —
 * including during the still-unsettled temp-id window (Sonnet-5 re-review, #5412/#5424 follow-up).
 * The settled-gate exists only to avoid a false id-comparison reading mid-swap; a dock close involves
 * no id comparison at all, so it is never ambiguous, and ending promptly on close matters here more
 * than for the settled case: while a session is active EVERY draft's Revise button is globally
 * disabled (`EmailDraftReviewCard`'s `disabled={busy || reviseActive}`), so leaving a closed-but-
 * unsettled session open would lock every other draft's Revise button for the rest of the in-flight
 * create window, AND — because ending the session makes `isReviseConversationActive` false — it also
 * makes the create's `shouldActivateOnResolve` come back false when it eventually resolves, so a
 * closed session's stale create can no longer force-navigate the user back into the abandoned chat.
 *
 * Called once from MainLayout, which already owns `useReviseSession()` and the session's
 * token/workspaceId.
 */
export function useReviseSessionReconciler(
  token: string | null | undefined,
  workspaceId: string,
): void {
  const { reviseActive, activeApprovalId, reviseConversationId, endRevise } = useReviseSession();
  const { activeConversation } = useConversation();
  const { dockState } = useChatDock();
  // Gate the poll on an active session — outside one the reconciler has nothing to do, so the
  // shared approvals query must not run idle. During a session the Activity Center is open and
  // observes the same key, so this dedupes with it (no extra request).
  const { items, isSuccess } = useActionApprovals(token, workspaceId, { enabled: reviseActive });

  // endRevise is stable (useCallback in the provider), but ref it so the effect depends only on the
  // reconcile inputs and never re-fires merely because the provider re-rendered.
  const endReviseRef = useRef(endRevise);
  endReviseRef.current = endRevise;

  // The query has settled AND the revised approval is no longer pending → its session is stale.
  const shouldEndByDecision =
    reviseActive &&
    !!activeApprovalId &&
    isSuccess &&
    !items.some((a) => a.id === activeApprovalId);

  // `reviseConversationId` is "settled" once it holds the real (post-create) conversation id rather
  // than the temp placeholder — comparing a temp id against `activeConversation.id` would false-fire
  // during the brief create round-trip, before the seed listener has confirmed the swap. Only the
  // SWITCH judgement below needs this gate (it's an id comparison); the CLOSE judgement doesn't
  // compare ids at all, so it is never subject to that ambiguity — see the file-header doc.
  const reviseConversationSettled =
    !!reviseConversationId && !reviseConversationId.startsWith('conversation-');
  const chatClosed = dockState === 'docked';
  const switchedAway = reviseConversationSettled && activeConversation?.id !== reviseConversationId;
  const shouldEndByCloseOrSwitch = reviseActive && (chatClosed || switchedAway);

  const shouldEnd = shouldEndByDecision || shouldEndByCloseOrSwitch;

  useEffect(() => {
    if (shouldEnd) endReviseRef.current();
  }, [shouldEnd]);
}
